import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  CohortRetentionSchema,
  type CohortRetention,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/cohorts/useCohorts';

export type CohortsState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; cohorts: CohortRetention[] };

/**
 * Lê /aggregations/cohorts/months/{yyyymm} entre `fromMonth` e
 * `toMonth` (inclusivos). Range usa documentId (yyyymm lex ==
 * cronológico).
 *
 * Origem: sem issue — commit 37823e3cb, 2026-05-04 (rota /dashboard/cohorts).
 *
 * Contrato: `fromMonth`/`toMonth` (`yyyymm`) → `CohortsState`, leitura
 * one-shot, ordenada por `cohortMonth`. O intervalo filtra o CAMPO
 * `cohortMonth`, não o `documentId()` que a linha acima cita (os dois valem o
 * mesmo `yyyymm`; divergência de texto registrada na #926). Doc fora do
 * schema é pulado só com `console.warn` e erro vira `ERROR` só com
 * `console.error` — sem report ao PostHog (achado na #926).
 *
 * Usado por: `CohortsDashboardPage.tsx` (rota `/dashboard/cohorts`; web
 * `apps/admin`, super-admin).
 *
 * Arquitetura: só leitura de coleção direta (não collection group), via
 * `match /aggregations/{type}/{subcoll}/{id}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); intervalo num campo só usa o índice automático.
 * Escritor: o scheduler `aggregateCohortRetention` (Functions) e o backfill.
 *
 * Dados (anonimizado; `CohortRetentionSchema` em
 * `packages/types/src/aggregations.ts`):
 *   aggregations/cohorts/months/{yyyymm}
 *     { "cohortMonth": "202609", "schemaVersion": 1, "cohortSize": 12,
 *       "retention": { … }, "cumulativeChurn": { … },
 *       "computedAt": 1759300000000, "computedBy": "aggregateCohortRetention" }
 *   — sem `labelsPrinted` de propósito: o CG `months` do
 *     `useAllTimePrintedGlobal` soma por cima desta coleção.
 */
export function useCohorts(fromMonth: string, toMonth: string): CohortsState {
  const [state, setState] = useState<CohortsState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });
    async function load(): Promise<void> {
      try {
        const ref = collection(firestore, 'aggregations', 'cohorts', 'months');
        const q = query(
          ref,
          where('cohortMonth', '>=', fromMonth),
          where('cohortMonth', '<=', toMonth),
        );
        const snap = await getDocs(q);
        if (guard.cancelled) return;
        const cohorts: CohortRetention[] = [];
        for (const d of snap.docs) {
          const parsed = CohortRetentionSchema.safeParse(d.data());
          if (parsed.success) cohorts.push(parsed.data);
          else console.warn(`${TAG}: ${d.id} ignorado`, parsed.error.issues);
        }
        cohorts.sort((a, b) => a.cohortMonth.localeCompare(b.cohortMonth));
        setState({ kind: 'READY', cohorts });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_cohorts_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [fromMonth, toMonth]);

  return state;
}
